import { useState, useCallback, useRef, useEffect } from 'react';
import { getConversationsByWorkflow, getAgentConfigFileIds } from './workflowData';
import { defaultSteps, getDefaultConfig } from './workflowHelpers';
import { getSavedWorkflows } from './workflowStorage';
import { fetchFileContent } from '../../../../utils/api';

export const CURRENT_WORKFLOW_NAME_KEY = 'bonzai-current-workflow-name';

export function useWorkflowEditor(workflow) {
  // Core state
  const [activeTab, setActiveTab] = useState('editor');
  const [steps, setSteps] = useState(workflow?.steps || defaultSteps);
  const [hasLoadedWorkflowSteps, setHasLoadedWorkflowSteps] = useState(!!workflow?.steps);
  const [isSaving, setIsSaving] = useState(false);
  const savedSnapshotRef = useRef(JSON.stringify(workflow?.steps || defaultSteps));
  const savedClaudeMdRef = useRef('');

  // DEBUG: Track all changes to steps
  useEffect(() => {
    console.log('[useWorkflowEditor] steps state changed:', {
      count: steps.length,
      steps: steps.map(s => ({ id: s.id, nodeId: s.nodeId, name: s.name }))
    });
  }, [steps]);

  // Wrap setSteps to log all calls
  const setStepsWithLogging = useCallback((newSteps) => {
    const trace = new Error().stack;
    console.log('[useWorkflowEditor] setSteps called from:', trace?.split('\n')[2]?.trim());
    const resolved = typeof newSteps === 'function' ? newSteps(steps) : newSteps;
    console.log('[useWorkflowEditor] setSteps changing from', steps.length, 'to', resolved.length);
    setSteps(newSteps);
  }, [steps]);

  // Track the current workflow ID to detect when user switches workflows
  const currentWorkflowId = useRef(workflow?.id);
  const defaultStepsCount = defaultSteps.length; // Should be 3

  useEffect(() => {
    const workflowId = workflow?.id;
    const workflowChanged = workflowId && workflowId !== currentWorkflowId.current;
    const workflowHasRealSteps = workflow?.steps && workflow.steps.length > defaultStepsCount;

    // Load workflow steps if:
    // 1. Workflow switched to a different one (user selected different workflow) OR
    // 2. Initial load with real steps (detected skills on startup) OR
    // 3. Initial load with any steps
    if (workflowChanged) {
      console.log('[useWorkflowEditor] Workflow switched, loading new steps:', workflowId, workflow.steps?.length);
      setSteps(workflow.steps || defaultSteps);
      savedSnapshotRef.current = JSON.stringify(workflow.steps || defaultSteps);
      currentWorkflowId.current = workflowId;
      setHasLoadedWorkflowSteps(true);
    }
    else if (!hasLoadedWorkflowSteps && workflowHasRealSteps) {
      console.log('[useWorkflowEditor] Loading detected workflow steps:', workflow.steps.length);
      setSteps(workflow.steps);
      savedSnapshotRef.current = JSON.stringify(workflow.steps);
      currentWorkflowId.current = workflowId;
      setHasLoadedWorkflowSteps(true);
    }
    else if (!hasLoadedWorkflowSteps && workflow?.steps) {
      console.log('[useWorkflowEditor] Loading default workflow steps:', workflow.steps.length);
      setSteps(workflow.steps);
      savedSnapshotRef.current = JSON.stringify(workflow.steps);
      currentWorkflowId.current = workflowId;
      setHasLoadedWorkflowSteps(true);
    }
  }, [workflow?.id, workflow?.steps, hasLoadedWorkflowSteps, defaultStepsCount]);

  // Log when workflow prop changes (for debugging)
  useEffect(() => {
    console.log('[useWorkflowEditor] workflow prop changed:', {
      hasWorkflowSteps: !!workflow?.steps,
      workflowStepsCount: workflow?.steps?.length || 0,
      currentLocalSteps: steps.length,
      currentWorkflowId: currentWorkflowId.current,
      newWorkflowId: workflow?.id
    });
  }, [workflow?.steps]);

  // Separate state for CLAUDE.md content
  const [claudeMdContent, setClaudeMdContent] = useState('');
  useEffect(() => {
    fetchFileContent('CLAUDE.md').then(content => {
      setClaudeMdContent(content || '');
      savedClaudeMdRef.current = content || '';
    }).catch(() => {});
  }, []);

  // Detect whether anything has changed since last save
  const hasUnsavedChanges = JSON.stringify(steps) !== savedSnapshotRef.current || claudeMdContent !== savedClaudeMdRef.current;

  // Workflow metadata
  const [workflowCreated, setWorkflowCreated] = useState(workflow?.created || new Date().toISOString());

  // UI state
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [isNodePanelOpen, setIsNodePanelOpen] = useState(true); // Default open
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [showExperiment, setShowExperiment] = useState(false);
  const [terminalHintClicked, setTerminalHintClicked] = useState(() => {
    return localStorage.getItem('bonzai-terminal-hint-clicked') === 'true';
  });
  const [performanceHintClicked, setPerformanceHintClicked] = useState(() => {
    return localStorage.getItem('bonzai-performance-hint-clicked') === 'true';
  });
  const [workflowSwitchHintClicked, setWorkflowSwitchHintClicked] = useState(() => {
    return localStorage.getItem('bonzai-workflow-switch-hint-clicked') === 'true';
  });

  // Wrapper functions to save hint state to localStorage
  const setTerminalHintClickedWithStorage = useCallback((value) => {
    setTerminalHintClicked(value);
    localStorage.setItem('bonzai-terminal-hint-clicked', value.toString());
  }, []);

  const setPerformanceHintClickedWithStorage = useCallback((value) => {
    setPerformanceHintClicked(value);
    localStorage.setItem('bonzai-performance-hint-clicked', value.toString());
  }, []);

  const setWorkflowSwitchHintClickedWithStorage = useCallback((value) => {
    setWorkflowSwitchHintClicked(value);
    localStorage.setItem('bonzai-workflow-switch-hint-clicked', value.toString());
  }, []);

  // Workflow management state
  const [currentWorkflowName, setCurrentWorkflowName] = useState(() => {
    return localStorage.getItem(CURRENT_WORKFLOW_NAME_KEY) || 'Untitled Workflow';
  });
  const [isEditingWorkflowName, setIsEditingWorkflowName] = useState(false);
  const [savedWorkflowsList, setSavedWorkflowsList] = useState(() => getSavedWorkflows());
  const [workflowTitle, setWorkflowTitle] = useState(workflow?.name || 'Select workflow');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDate, setSaveDate] = useState('');
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState(() => getConversationsByWorkflow(workflow?.id) || []);
  const [hoveredConversation, setHoveredConversation] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [expandedFiles, setExpandedFiles] = useState({});
  const [autoUpdate, setAutoUpdate] = useState(true);

  // Other state
  const [showWorkflowMenu, setShowWorkflowMenu] = useState(false);
  const [isRenamingWorkflow, setIsRenamingWorkflow] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const workflowMenuRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Close workflow menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (workflowMenuRef.current && !workflowMenuRef.current.contains(e.target)) {
        setShowWorkflowMenu(false);
        setIsRenamingWorkflow(false);
      }
    }
    if (showWorkflowMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showWorkflowMenu]);

  // Handlers
  const getCurrentAgentType = useCallback(() => {
    const responseStep = steps.find(s => s.isEnd || s.nodeId === 'response');
    const outputMode = responseStep?.config?.outputMode;
    if (outputMode === 'claude' || outputMode === 'cursor') {
      return outputMode;
    }
    return workflow?.agentType || 'claude';
  }, [steps, workflow?.agentType]);

  const handleStepHover = useCallback((step, onStepHover) => {
    if (onStepHover) {
      let fileIds = step?.fileIds || [];
      const agentType = getCurrentAgentType();

      if (step?.isEnd || step?.nodeId === 'response') {
        if (agentType) {
          const configFileIds = getAgentConfigFileIds(agentType);
          fileIds = [...new Set([...fileIds, ...configFileIds])];
        }
      } else if (step?.nodeId === 'code' || step?.nodeId === 'hooks') {
        if (agentType === 'claude') {
          fileIds = [...new Set([...fileIds, 'claude-config', 'claude-hooks', 'claude-hook-precommit', 'claude-hook-postpush', 'claude-hook-onresponse'])];
        } else if (agentType === 'cursor') {
          fileIds = [...new Set([...fileIds, 'cursor-config', 'cursor-tools', 'cursor-tool-lint', 'cursor-tool-migrate'])];
        }
      } else if (step?.nodeId === 'requirement' || step?.nodeId === 'spec' || step?.nodeId === 'rule') {
        if (agentType === 'claude') {
          fileIds = [...new Set([...fileIds, 'claude-config', 'claude-skills', 'claude-skill-deploy', 'claude-skill-review'])];
        } else if (agentType === 'cursor') {
          fileIds = [...new Set([...fileIds, 'cursor-config', 'cursor-rules', 'cursor-rule-global', 'cursor-rule-react', 'cursor-rule-testing'])];
        }
      }

      onStepHover(fileIds);
    }
  }, [getCurrentAgentType]);

  const handleSelectStep = useCallback((stepId) => {
    setSelectedStepId(stepId);
    setIsNodePanelOpen(false);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedStepId(null);
  }, []);

  const handleOpenNodePanel = useCallback(() => {
    setIsNodePanelOpen(true);
    setInsertAtIndex(null);
    setSelectedStepId(null);
  }, []);

  const handleInsertAtConnector = useCallback((stepIndex) => {
    setInsertAtIndex(stepIndex);
    setIsNodePanelOpen(true);
    setSelectedStepId(null);
  }, []);

  const handleCloseNodePanel = useCallback(() => {
    setIsNodePanelOpen(false);
    setInsertAtIndex(null);
  }, []);

  const handleDropNode = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const node = JSON.parse(e.dataTransfer.getData('application/json'));
      const newStepId = `step-${Date.now()}`;
      setSteps(prev => {
        const endIdx = prev.findIndex(s => s.isEnd);
        const targetIndex = endIdx === -1 ? prev.length : endIdx;
        const parentStep = targetIndex > 0 ? prev[targetIndex - 1] : null;

        const defaultConfig = getDefaultConfig(node.id, node);
        const skillName = defaultConfig.skillName || node.id;

        const newStep = {
          id: newStepId,
          nodeId: node.id,
          name: node.name,
          subtitle: node.description,
          icon: node.icon,
          iconBg: `${node.color}15`,
          parentNode: parentStep ? parentStep.id : null,
          filepath: node.filepath || `.claude/skills/${skillName}/SKILL.md`,
          isPublished: node.isPublished || false,
          rating: node.rating || 0,
          config: defaultConfig,
          fileIds: []
        };

        const newSteps = [...prev];
        newSteps.splice(targetIndex, 0, newStep);
        if (targetIndex + 1 < newSteps.length) {
          newSteps[targetIndex + 1] = { ...newSteps[targetIndex + 1], parentNode: newStep.id };
        }
        return newSteps;
      });
      setSelectedStepId(newStepId);
    } catch {}
  }, []);

  const handleConfigChange = useCallback((newConfig) => {
    setSteps(prev => {
      const editingStep = prev.find(s => s.id === selectedStepId);
      if (editingStep?.isLoopStart && newConfig.template !== undefined) {
        setClaudeMdContent(newConfig.template);
      }

      return prev.map(s => {
        if (s.id !== selectedStepId) return s;

        let updatedStep = { ...s, config: newConfig };

        // Sync template node fields from config
        if (newConfig.title !== undefined && newConfig.title) {
          updatedStep.name = newConfig.title;
        }
        if (newConfig.description !== undefined && newConfig.description) {
          updatedStep.subtitle = newConfig.description;
        }
        if (newConfig.icon !== undefined && newConfig.icon) {
          updatedStep.icon = newConfig.icon;
        }
        if (newConfig.skillName) {
          updatedStep.filepath = `.claude/skills/${newConfig.skillName}/SKILL.md`;
        }

        // Prompt node handling
        if (s.nodeId === 'prompt' && newConfig.inputLocation) {
          updatedStep.fileName = newConfig.inputLocation.charAt(0).toUpperCase() + newConfig.inputLocation.slice(1);
        }

        // Response node handling
        if (s.nodeId === 'response') {
          if (newConfig.model) {
            updatedStep.fileName = newConfig.model.charAt(0).toUpperCase() + newConfig.model.slice(1);
          }
          if (newConfig.outputMode) {
            const subtitleMap = {
              'claude': 'Claude CLI',
              'cursor': 'Cursor AI',
              'api': 'API Key',
              'readonly': 'No Write Access'
            };
            updatedStep.subtitle = subtitleMap[newConfig.outputMode] || 'Deliver results';
          }
        }

        return updatedStep;
      });
    });
  }, [selectedStepId]);

  const handleDeleteStep = useCallback(async (deleteFileFn) => {
    setSteps(prev => {
      const step = prev.find(s => s.id === selectedStepId);

      // If it's the CLAUDE.md init step, delete the file
      if (step?.isLoopStart && deleteFileFn) {
        deleteFileFn('CLAUDE.md')
          .then(() => {
            setClaudeMdContent('');
            setSelectedStepId(null);
          })
          .catch(err => console.error('Failed to delete CLAUDE.md:', err));
        return prev;
      }

      setSelectedStepId(null);
      return prev.filter(s => s.id !== selectedStepId);
    });
  }, [selectedStepId]);

  return {
    // State
    activeTab,
    steps,
    isSaving,
    hasUnsavedChanges,
    claudeMdContent,
    workflowCreated,
    selectedStepId,
    isNodePanelOpen,
    insertAtIndex,
    isDragOver,
    terminalOpen,
    showExperiment,
    terminalHintClicked,
    performanceHintClicked,
    workflowSwitchHintClicked,
    currentWorkflowName,
    isEditingWorkflowName,
    savedWorkflowsList,
    workflowTitle,
    showSaveModal,
    saveName,
    saveDate,
    editingWorkflowId,
    chatInput,
    chatMessages,
    hoveredConversation,
    hoveredFile,
    expandedLogs,
    expandedFiles,
    autoUpdate,
    showWorkflowMenu,
    isRenamingWorkflow,
    renameValue,
    workflowMenuRef,
    messagesEndRef,
    savedSnapshotRef,
    savedClaudeMdRef,

    // Setters
    setActiveTab,
    setSteps: setStepsWithLogging,
    setIsSaving,
    setClaudeMdContent,
    setWorkflowCreated,
    setSelectedStepId,
    setIsNodePanelOpen,
    setInsertAtIndex,
    setIsDragOver,
    setTerminalOpen,
    setShowExperiment,
    setTerminalHintClicked: setTerminalHintClickedWithStorage,
    setPerformanceHintClicked: setPerformanceHintClickedWithStorage,
    setWorkflowSwitchHintClicked: setWorkflowSwitchHintClickedWithStorage,
    setCurrentWorkflowName,
    setIsEditingWorkflowName,
    setSavedWorkflowsList,
    setWorkflowTitle,
    setShowSaveModal,
    setSaveName,
    setSaveDate,
    setEditingWorkflowId,
    setChatInput,
    setChatMessages,
    setHoveredConversation,
    setHoveredFile,
    setExpandedLogs,
    setExpandedFiles,
    setAutoUpdate,
    setShowWorkflowMenu,
    setIsRenamingWorkflow,
    setRenameValue,

    // Handlers
    getCurrentAgentType,
    handleStepHover,
    handleSelectStep,
    handleClosePanel,
    handleOpenNodePanel,
    handleInsertAtConnector,
    handleCloseNodePanel,
    handleDropNode,
    handleConfigChange,
    handleDeleteStep,
  };
}
